import { useNavigate } from "react-router-dom";
import { X, Sparkles, BookOpen, CalendarDays, Mic2, Clapperboard, HeartHandshake, Compass, MessageCircleQuestion } from "lucide-react";
import { IMG } from "@/lib/images";
import { track } from "@/lib/analytics";

const INTENTS = [
  { key: "consulter", label: "Je souhaite consulter", desc: "Voyance, médiumnité, onirothérapie…", Icon: Sparkles, to: "/consultations" },
  { key: "livres", label: "Découvrir ses livres", desc: "Romans, essais, poésie, ésotérisme", Icon: BookOpen, to: "/livres" },
  { key: "evenement", label: "La rencontrer lors d'un événement", desc: "Salons, dédicaces, agenda", Icon: CalendarDays, to: "/agenda" },
  { key: "conference", label: "L'inviter pour une conférence", desc: "Salons, écoles, rencontres publiques", Icon: Mic2, demande: "conference" },
  { key: "media", label: "Je suis journaliste / média", desc: "Espace presse & kit média", Icon: Clapperboard, to: "/kit-media" },
  { key: "partenariat", label: "Proposer un partenariat", desc: "Collaboration, projet commun", Icon: HeartHandshake, demande: "partenariat" },
  { key: "univers", label: "Découvrir son univers", desc: "Les Chroniques de l'Étrange, vidéos", Icon: Compass, to: "/univers" },
];

export default function ConciergeModal({ open, onClose, openDemande }) {
  const navigate = useNavigate();
  if (!open) return null;

  const choose = (it) => {
    track("concierge_intent", { intent: it.key, source: "concierge" });
    onClose();
    if (it.demande) { openDemande?.(it.demande); }
    else if (it.to) { navigate(it.to); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="concierge-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl bg-[#0B1020] border border-[rgba(198,167,106,0.35)] dl-bubble max-h-[92vh] overflow-y-auto">
        <div className="p-7 md:p-9">
          <button data-testid="concierge-close" onClick={onClose} className="absolute top-4 right-4 text-[#F3E9D2]/60 hover:text-[#F3E9D2]"><X size={22} /></button>

          <div className="flex items-center gap-4">
            <img src={IMG.iaPortrait || IMG.portrait} alt="Assistante numérique inspirée de Dorothée Lancelot" className="w-14 h-14 rounded-full object-cover border border-[rgba(198,167,106,0.4)]" loading="lazy" />
            <div>
              <div className="dl-eyebrow mb-1">Assistante numérique</div>
              <h2 className="serif text-2xl md:text-3xl text-[#F3E9D2] leading-tight">Que recherchez-vous aujourd'hui ?</h2>
            </div>
          </div>
          <p className="text-xs text-[#F3E9D2]/45 mt-3">
            Je suis un assistant numérique inspiré de Dorothée Lancelot — je ne suis pas Dorothée elle-même. Je vous oriente vers le bon endroit en un clic.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
            {INTENTS.map((it) => (
              <button
                key={it.key}
                data-testid={`concierge-intent-${it.key}`}
                onClick={() => choose(it)}
                className="group text-left border border-[rgba(198,167,106,0.25)] p-4 hover:border-[#C6A76A] hover:bg-white/[0.04] transition-colors flex gap-3"
              >
                <it.Icon size={20} className="text-[#C6A76A] shrink-0 mt-0.5" />
                <span>
                  <span className="block text-[#F3E9D2] group-hover:text-[#D4B978] transition-colors leading-tight">{it.label}</span>
                  <span className="block text-xs text-[#F3E9D2]/50 mt-1">{it.desc}</span>
                </span>
              </button>
            ))}
          </div>

          <button
            data-testid="concierge-ia"
            onClick={() => { track("concierge_intent", { intent: "assistant", source: "concierge" }); onClose(); navigate("/dorothee-ia"); }}
            className="mt-4 w-full flex items-center justify-center gap-2 border border-[rgba(198,167,106,0.3)] px-4 py-3 text-sm text-[#F3E9D2]/85 hover:bg-white/[0.04] transition-colors"
          >
            <MessageCircleQuestion size={16} className="text-[#C6A76A]" /> Poser une question à Dorothée IA
          </button>
        </div>
      </div>
    </div>
  );
}
